import { useEffect, useRef, useState } from "react";
import { ChevronDown, Check } from "lucide-react";

/**
 * Single-select dropdown that visually matches `MultiSelect.jsx`. Use this in
 * place of native `<select>` wherever a one-of-many choice is needed (e.g.
 * "Sort by"), so every dropdown on the public site shares one design.
 *
 * Props:
 *   options    – array of { id, label }
 *   value      – currently selected `id`
 *   onChange   – (id: string) => void
 *   placeholder, testId
 */
export function SingleSelect({ options = [], value = "", onChange, placeholder = "Select…", testId }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const pick = (id) => {
    onChange?.(id);
    setOpen(false);
  };
  const labelOf = (id) => options.find((o) => o.id === id)?.label ?? id;
  const summary = value ? labelOf(value) : placeholder;

  return (
    <div className="relative" ref={ref} data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-line bg-white text-sm hover:border-forest/40 transition-colors"
        data-testid={testId ? `${testId}-trigger` : undefined}
      >
        <span className={`truncate ${value ? "text-ink" : "text-muted_text"}`}>{summary}</span>
        <ChevronDown className={`w-4 h-4 text-muted_text shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute z-30 left-0 right-0 mt-1 border border-line bg-white shadow-lg max-h-72 overflow-auto" data-testid={testId ? `${testId}-panel` : undefined}>
          {options.length === 0 ? (
            <div className="px-3 py-2 text-xs text-muted_text">No options</div>
          ) : (
            <ul className="py-1">
              {options.map((o) => {
                const active = value === o.id;
                return (
                  <li key={o.id}>
                    <button
                      type="button"
                      onClick={() => pick(o.id)}
                      className="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-sm text-left hover:bg-sand/60 transition-colors"
                      data-testid={testId ? `${testId}-option-${o.id}` : undefined}
                    >
                      <span className="truncate">{o.label}</span>
                      {active && <Check className="w-3.5 h-3.5 text-forest shrink-0" />}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
